import { Controller } from "@hotwired/stimulus"
import MoreMessages from "../lib/more_messages"

const STEP_TENT = "tent"
const STEP_READY = "ready"
const STEP_SIZE = "size"
const REDRAW_DELAY_MS = 150
const NONE = "none"
const SEATING_POPUP_MS = 4000
const SEATING_ROUND = "round"
const SEATING_LONG = "long"
const SEATING_THEATRE = "theatre"
const TENT_FRAME = "frame"
const TENT_TIPI = "tipi"
const TIPI_SIDES_CLOSED = "closed"
// A step of length: a frame tent's 3 m bay, or a pole tent's 5 m canopy section (the sections between a sail's round ends). These
// are only for what the panel shows before the frame says what it drew (`summary` carries the real length).
const STEP_M = { frame: 3, traditional: 5, sail: 5 }
const MIN_STEPS = { frame: 2, traditional: 2, sail: 1, tipi: 1 }
// A tipi's steps are hats: one to six. One is alone, two a line, three and four a clover, five and six a cluster of two columns.
const TIPI_MAX_HATS = 6
const MAX_STEPS = { frame: 30, traditional: 30, sail: 30, tipi: TIPI_MAX_HATS }
// A pole tent's section length as the visitor picks it (metres); 5 m is the default. The 20 ft and 25 ft sections are drawn at their real
// lengths, which only the frame knows (`summary` carries them).
const DEFAULT_SECTION_M = 5
const GUEST_STEP = 10
const MAX_GUESTS = 300
const SEATS_PER_TABLE = 8

// The guided "Plan your event" flow on the Layout tab. It owns the answers; the layout-planner controller owns the frame.
// Every answer asks the gallery for a fresh plan (`layout-guided:plan`), which floorplans builds from the same layout code the
// editor uses, so the plan on the right is the real thing. When the frame is up, the gallery hands the numbers it drew back
// (`summary`), and the tiles and the fit message come from those, never from a guess made here.
export default class extends Controller {
  static targets = [
    "step", "chips", "progressLabel", "progressBar", "backButton", "continueButton", "footer",
    "widthOption", "guestOption", "extraOption", "liningOption", "flooringOption", "seatingOption", "seatingPopup", "doorOption", "wallOption", "roofOption", "sectionOption", "tipiSidesOption", "tipiExtensionOption", "tipiFairyLightsOption", "lengthValue", "baysNote", "guestsValue",
    "fitNote", "grownNote", "unplacedNote", "demoNote", "tentOnly", "statSeated", "statTables", "statTablesLabel", "statArea", "needList"
  ]
  static values = { steps: Array, defaults: Object, texts: Object, floorplansOrigin: String }

  connect() {
    this.more = new MoreMessages(this.element)
    this.start()
    // The "pick a number of guests first" message goes on the next click anywhere but the seating chips.
    this.onDocumentClick = (event) => { if (!event.target.closest("[data-layout-guided-target~=seatingOption]")) this.hideSeatingPopup() }
    document.addEventListener("click", this.onDocumentClick)
  }

  // On load, and again on "Start over".
  start() {
    clearTimeout(this.redrawTimer)
    this.answers = { tent: TENT_FRAME, width: this.defaultsValue.width, bays: this.defaultsValue.bays[TENT_FRAME], section: DEFAULT_SECTION_M, guests: 0, extras: [], lining: NONE, flooring: NONE, seating: SEATING_ROUND, doors: NONE, clearWalls: NONE, clearRoof: NONE, tipiSides: TIPI_SIDES_CLOSED, tipiExtension: false, tipiFairyLights: false }
    this.summaryData = null
    this.sizeChanged = false
    this.show(STEP_TENT)
  }

  disconnect() {
    clearTimeout(this.redrawTimer)
    clearTimeout(this.popupTimer)
    document.removeEventListener("click", this.onDocumentClick)
  }

  // ── Answers ──

  // A tent type has its own length steps and its own options: the length goes back to the type's own start, and the doors and clear
  // panels chosen for another type are put away, so a tent is never given what its type does not take. Long tables need a straight run, which a tipi has
  // not, so they go back to round tables.
  chooseTent(event) {
    const tent = event.currentTarget.dataset.value
    const seating = tent === TENT_TIPI && this.answers.seating === SEATING_LONG ? SEATING_ROUND : this.answers.seating
    // A tipi has no lining either (see the finishes step), so what was chosen for another tent is not carried over.
    const lining = tent === TENT_TIPI ? NONE : this.answers.lining
    this.answers = { ...this.answers, tent, seating, lining, bays: this.defaultsValue.bays[tent], section: DEFAULT_SECTION_M, doors: NONE, clearWalls: NONE, clearRoof: NONE, tipiSides: TIPI_SIDES_CLOSED, tipiExtension: false, tipiFairyLights: false }
    this.summaryData = null
    this.sizeChanged = false
    this.redraw()
    this.show(STEP_SIZE)
  }

  setWidth(event) { this.change({ width: Number(event.currentTarget.dataset.value) }) }
  setSection(event) { this.change({ section: Number(event.currentTarget.dataset.value) }) }
  moreBays() { this.stepSize(1) }
  fewerBays() { this.stepSize(-1) }

  // + and − start from the size on the canvas, so they always move what the visitor can see; but once they have been pressed the plan
  // for that is still being drawn, so the next press starts from the size just asked for, or quick presses would not add up.
  stepSize(direction) {
    const bays = Math.min(Math.max(this.shownBays + direction, MIN_STEPS[this.answers.tent]), MAX_STEPS[this.answers.tent])
    this.sizeChanged = true
    this.change({ bays })
  }
  setGuests(event) { this.change({ guests: Number(event.currentTarget.dataset.value) }) }
  moreGuests() { this.change({ guests: Math.min(this.answers.guests + GUEST_STEP, MAX_GUESTS) }) }
  fewerGuests() { this.change({ guests: Math.max(this.answers.guests - GUEST_STEP, 0) }) }

  toggleExtra(event) {
    const extra = event.currentTarget.dataset.value
    const extras = this.answers.extras.includes(extra) ? this.answers.extras.filter((name) => name !== extra) : [...this.answers.extras, extra]
    this.change({ extras })
  }

  setLining(event) { this.change({ lining: event.currentTarget.dataset.value }) }
  // Seating is locked until there are guests to seat: the chips say so (aria-disabled) and a click or key press on one explains why.
  setSeating(event) {
    if (this.answers.guests === 0) return this.showSeatingPopup()

    this.change({ seating: event.currentTarget.dataset.value })
  }

  showSeatingPopup() {
    this.seatingPopupTarget.hidden = false
    clearTimeout(this.popupTimer)
    this.popupTimer = setTimeout(() => this.hideSeatingPopup(), SEATING_POPUP_MS)
  }

  hideSeatingPopup() {
    clearTimeout(this.popupTimer)
    if (this.hasSeatingPopupTarget) this.seatingPopupTarget.hidden = true
  }
  setDoors(event) { this.change({ doors: event.currentTarget.dataset.value }) }
  setTipiSides(event) { this.change({ tipiSides: event.currentTarget.dataset.value }) }
  toggleTipiExtension() { this.change({ tipiExtension: !this.answers.tipiExtension }) }
  toggleTipiFairyLights() { this.change({ tipiFairyLights: !this.answers.tipiFairyLights }) }
  setClearWalls(event) { this.change({ clearWalls: event.currentTarget.dataset.value }) }
  setClearRoof(event) { this.change({ clearRoof: event.currentTarget.dataset.value }) }
  setFlooring(event) { this.change({ flooring: event.currentTarget.dataset.value }) }

  change(answer) {
    this.answers = { ...this.answers, ...answer }
    this.render()
    this.scheduleRedraw()
  }

  // ── Moving between questions ──

  next() {
    const index = this.stepsValue.indexOf(this.step)
    this.show(this.stepsValue[Math.min(index + 1, this.stepsValue.length - 1)])
  }

  back() {
    const index = this.stepsValue.indexOf(this.step)
    this.show(this.stepsValue[Math.max(index - 1, 0)])
  }

  // ── "More": the last chip of every row of options (see `MoreMessages`) ──
  toggleMore(event) { this.more.toggle(event.currentTarget) }
  closeMore(event) { this.more.close(event.currentTarget) }
  moreKeydown(event) { this.more.keydown(event) }

  goTo(event) {
    this.show(event.currentTarget.dataset.step)
  }

  show(step) {
    this.step = step
    this.hideSeatingPopup()
    this.more.closeAll()
    this.stepTargets.forEach((section) => { section.hidden = section.dataset.step !== step })
    this.render()
  }

  // ── The plan ──

  scheduleRedraw() {
    clearTimeout(this.redrawTimer)
    this.redrawTimer = setTimeout(() => this.redraw(), REDRAW_DELAY_MS)
  }

  redraw() {
    this.dispatch("plan", { detail: { embedUrl: this.embedUrl() } })
  }

  embedUrl() {
    const { tent, width, bays, section, guests, extras, lining, flooring, seating, doors, clearWalls, clearRoof } = this.answers
    const params = new URLSearchParams({ tent, width, bays, guests, lining, flooring, seating, doors, clear_walls: clearWalls, clear_roof: clearRoof })
    // Only a pole tent has sections to choose the length of.
    if (this.polesTent) params.append("section_m", section)
    // A tipi's choices are its alone, so no other tent is sent them.
    if (tent === TENT_TIPI) params.append("tipi_sides", this.answers.tipiSides)
    if (tent === TENT_TIPI) params.append("tipi_extension", this.answers.tipiExtension)
    if (tent === TENT_TIPI) params.append("tipi_fairy_lights", this.answers.tipiFairyLights)
    extras.forEach((extra) => params.append("extras[]", extra))
    return `${this.floorplansOriginValue}/embed/guided?${params}`
  }

  // What the frame drew: { tables, guests_seated, area_m2, all_guests_fit, bays, grown, ... }.
  //
  // ⚠️ THE LENGTH ASKED FOR IS A MINIMUM. The tent grows to fit the guests and comes back down to what was asked for when there are
  // fewer, so the answer stays what the visitor set and the size on the canvas is read from here. Pressing + or − starts from the
  // size on the canvas, so it always moves what they can see.
  summary(data) {
    // A plan for the tent type the visitor has since moved off is not what is on the canvas any more.
    if (data.tent !== this.answers.tent) return

    this.summaryData = data
    this.sizeChanged = false
    this.render()
  }

  get drawnBays() {
    return this.summaryData?.bays ?? this.answers.bays
  }

  // What the size stepper shows: the size just asked for until the plan for it is drawn, then the size drawn.
  get shownBays() {
    return this.sizeChanged ? this.answers.bays : this.drawnBays
  }

  // The length on the canvas, in metres: what the frame says it drew, or until it has, the same sum made here. A sail tent is its
  // width plus its sections, because the two round ends are half a width each. (A tipi is counted in hats, not metres.)
  get drawnLength() {
    if (this.summaryData && !this.sizeChanged) return this.summaryData.length_m

    const { tent, width, bays, section } = this.answers
    return (tent === "sail" ? width : 0) + (bays * (this.polesTent ? section : STEP_M[tent]))
  }

  // A traditional or sail tent: the two with canopy sections on poles.
  get polesTent() {
    return this.answers.tent === "traditional" || this.answers.tent === "sail"
  }

  // ── Drawing the panel ──

  render() {
    const index = this.stepsValue.indexOf(this.step)
    const questions = this.stepsValue.length - 1
    const onReady = this.step === STEP_READY
    this.progressLabelTarget.textContent = onReady ? "" : this.text("step_of", { step: index + 1, total: questions })
    this.progressBarTarget.style.width = `${(Math.min(index + 1, questions) / questions) * 100}%`
    // Only the step label and bar go on the ready screen: on a phone the progress row also holds "Start over".
    this.progressLabelTarget.hidden = onReady
    this.progressBarTarget.parentElement.hidden = onReady
    this.footerTarget.hidden = this.step === STEP_TENT
    this.backButtonTarget.hidden = onReady
    this.continueButtonTarget.hidden = onReady
    const lastQuestion = this.step === this.stepsValue[this.stepsValue.length - 2]
    this.continueButtonTarget.textContent = lastQuestion ? this.continueButtonTarget.dataset.seePlan : this.continueButtonTarget.dataset.continue
    this.continueButtonTarget.disabled = this.step === "guests" && this.answers.guests === 0

    this.widthOptionTargets.forEach((option) => this.mark(option, Number(option.dataset.value) === this.answers.width))
    this.guestOptionTargets.forEach((option) => this.mark(option, Number(option.dataset.value) === this.answers.guests))
    this.extraOptionTargets.forEach((option) => this.mark(option, this.answers.extras.includes(option.dataset.value)))
    this.liningOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.lining))
    this.flooringOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.flooring))
    this.seatingOptionTargets.forEach((option) => {
      this.mark(option, option.dataset.value === this.answers.seating)
      option.setAttribute("aria-disabled", String(this.answers.guests === 0))
    })
    if (this.answers.guests > 0) this.hideSeatingPopup()
    this.doorOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.doors))
    this.wallOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.clearWalls))
    this.roofOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.clearRoof))
    this.sectionOptionTargets.forEach((option) => this.mark(option, Number(option.dataset.value) === this.answers.section))
    this.tipiSidesOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.tipiSides))
    this.mark(this.tipiExtensionOptionTarget, this.answers.tipiExtension)
    this.mark(this.tipiFairyLightsOptionTarget, this.answers.tipiFairyLights)
    this.lengthValueTarget.textContent = this.answers.tent === TENT_TIPI ? this.hatsLabel(this.shownBays) : `${this.drawnLength} m`
    this.baysNoteTarget.textContent = this.text(`bays_${this.answers.tent}`, { count: this.shownBays, section: this.answers.section })
    this.guestsValueTarget.textContent = this.answers.guests
    this.tentOnlyTargets.forEach((element) => { element.hidden = !element.dataset.tents.split(" ").includes(this.answers.tent) })
    this.renderChips()
    this.renderGrownNote()
    this.renderUnplacedNote()
    this.renderDemoNote()
    this.renderReady()
  }

  // A tipi demo goes up to six hats. At six hats, or when even six cannot seat everyone, the visitor is told it is a demo, not that the
  // product stops there.
  renderDemoNote() {
    const atTheLimit = this.answers.tent === TENT_TIPI && (this.drawnBays >= TIPI_MAX_HATS || this.summaryData?.all_guests_fit === false)
    this.demoNoteTarget.hidden = !atTheLimit
    if (atTheLimit) this.demoNoteTarget.textContent = this.text("demo_note")
  }

  renderGrownNote() {
    const grown = this.summaryData?.grown
    this.grownNoteTarget.hidden = !grown
    if (!grown) return

    this.grownNoteTarget.textContent = this.answers.tent === TENT_TIPI ? this.text("grown_tipi", { hats: this.hatsLabel(this.summaryData.bays) }) : this.text("grown", { length: this.summaryData.length_m })
  }

  // Extras the layout found no room for are not drawn (a dance floor between a pole tent's centre poles, say), so the visitor is told.
  renderUnplacedNote() {
    const unplaced = this.unplacedExtras
    this.unplacedNoteTarget.hidden = unplaced.length === 0
    if (unplaced.length) this.unplacedNoteTarget.textContent = this.text("unplaced", { names: unplaced.map((extra) => this.text(`extras_${extra}`)).join(", ") })
  }

  get unplacedExtras() {
    return (this.summaryData?.unplaced_extras ?? []).filter((extra) => this.answers.extras.includes(extra))
  }

  mark(option, on) {
    option.setAttribute("aria-pressed", String(on))
  }

  // The answers so far, as chips; a tap goes back to that question and the plan keeps what it can.
  renderChips() {
    const chips = []
    if (this.step !== STEP_TENT) chips.push([STEP_TENT, this.text(`chip_tent_${this.answers.tent}`)])
    if (!this.before(STEP_SIZE)) chips.push([STEP_SIZE, this.sizeChip()])
    if (!this.before("extras") && this.answers.guests) chips.push(["guests", this.text("chip_guests", { count: this.answers.guests })])

    this.chipsTarget.replaceChildren(...chips.map(([step, label]) => this.chip(step, label)))
  }

  // "12 × 15 m", or for a tipi "2 hats · 163 m²" (the area once the frame has drawn it).
  sizeChip() {
    if (this.answers.tent !== TENT_TIPI) return this.text("chip_size", { width: this.answers.width, length: this.drawnLength })
    if (!this.summaryData) return this.hatsLabel(this.drawnBays)

    return this.text("chip_size_tipi", { hats: this.hatsLabel(this.drawnBays), area: this.summaryData.area_m2 })
  }

  hatsLabel(count) {
    return this.text(count === 1 ? "hats_one" : "hats_other", { count })
  }

  chip(step, label) {
    const chip = document.createElement("button")
    chip.type = "button"
    chip.className = "fp-guided__chip"
    chip.dataset.step = step
    chip.dataset.action = "layout-guided#goTo"
    chip.textContent = label
    return chip
  }

  before(step) {
    return this.stepsValue.indexOf(this.step) <= this.stepsValue.indexOf(step)
  }

  renderReady() {
    const data = this.summaryData
    if (!data) return

    const guests = this.answers.guests
    this.statSeatedTarget.textContent = data.guests_seated
    this.statTablesTarget.textContent = data.tables
    this.statTablesLabelTarget.textContent = this.text(`units_${data.seating}`)
    this.statAreaTarget.textContent = `${data.area_m2} m²`
    this.fitNoteTarget.textContent = this.fitText(data, guests)
    this.needListTarget.replaceChildren(...this.needLines(data).map(([qty, name]) => this.needLine(qty, name)))
  }

  fitText(data, guests) {
    if (!guests) return this.text("no_guests")

    if (data.all_guests_fit) return this.text("fits", { count: guests })

    return this.text(data.tent === TENT_TIPI ? "does_not_fit_tipi" : "does_not_fit", { seated: data.guests_seated, count: guests })
  }

  needLines(data) {
    const lines = [[1, this.tentLine(data)]]
    lines.push(...this.seatingLines(data))
    if (data.doors) lines.push([data.doors, this.text("need_doors")])
    if (data.clear_wall_panels) lines.push([data.clear_wall_panels, this.text("need_clear_walls")])
    if (data.clear_roof_panels) lines.push([data.clear_roof_panels, this.text("need_clear_roof")])
    if (data.tipi_open_sides) lines.push([data.tipi_open_sides, this.text("need_tipi_open_sides")])
    if (data.tipi_window_extension) lines.push([1, this.text("need_tipi_extension")])
    if (data.tipi_fairy_lights) lines.push([1, this.text("need_tipi_fairy_lights")])
    this.answers.extras.filter((extra) => !this.unplacedExtras.includes(extra)).forEach((extra) => lines.push([1, this.text(`need_${extra}`)]))
    if (this.answers.lining !== NONE) lines.push([1, this.text("need_lining", { name: this.labelOf(this.liningOptionTargets, this.answers.lining) })])
    if (this.answers.flooring !== NONE) lines.push([1, this.text("need_flooring", { name: this.labelOf(this.flooringOptionTargets, this.answers.flooring) })])
    return lines
  }

  // The tent, as one line of the list: its size for a tent with sides, "Stratus 72 tipi x2" for a tipi.
  tentLine(data) {
    if (data.tent === TENT_TIPI) return this.text(data.bays === 1 ? "need_tent_tipi_one" : "need_tent_tipi", { count: data.bays })

    return this.text(`need_tent_${data.tent}`, { width: data.width_m, length: data.length_m })
  }

  // What the seating needs: tables and a chair each, or chairs in rows.
  seatingLines(data) {
    const chairs = [data.guests_seated, this.text("need_chairs")]
    if (data.seating === SEATING_THEATRE) return data.guests_seated ? [chairs] : []
    if (!data.tables) return []

    return [[data.tables, this.text(data.seating === SEATING_LONG ? "need_long_tables" : "need_tables")], chairs]
  }

  labelOf(options, value) {
    return options.find((option) => option.dataset.value === value).textContent
  }

  needLine(qty, name) {
    const item = document.createElement("li")
    const count = document.createElement("strong")
    count.textContent = typeof qty === "number" ? `${qty}×` : qty
    item.append(count, document.createTextNode(name))
    return item
  }

  text(key, values = {}) {
    return Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), this.textsValue[key])
  }
}
